'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

const EmojiMartReadyContext = createContext<boolean>(false);

export function useEmojiMartReady(): boolean {
  return useContext(EmojiMartReadyContext);
}

/** Set of native emoji strings that em-emoji can render (populated at init) */
let knownNatives: Set<string> | null = null;

/** Check whether em-emoji has this emoji in its dataset */
export function isKnownNativeEmoji(native: string): boolean {
  return knownNatives?.has(native) ?? false;
}

/** Convert a native emoji string to a CDN-compatible unified hex code.
 *  Strips variation selector U+FE0F — Apple emoji filenames omit it. */
export function nativeToUnified(native: string): string {
  return [...native]
    .map((cp) => cp.codePointAt(0)!)
    .filter((cp) => cp !== 0xfe0f)
    .map((cp) => cp.toString(16))
    .join('-');
}

/** Apple emoji CDN image URL */
export function getAppleEmojiUrl(native: string): string {
  return `https://cdn.jsdelivr.net/npm/emoji-datasource-apple@16.0.0/img/apple/64/${nativeToUnified(native)}.png`;
}

interface EmojiMartProviderProps {
  children: React.ReactNode;
}

export function EmojiMartProvider({ children }: EmojiMartProviderProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([import('@emoji-mart/data'), import('emoji-mart')])
      .then(([dataModule, emojiMart]) => {
        if (cancelled) return;

        const data = dataModule.default as {
          emojis: Record<string, { skins: { native?: string }[] }>;
        };

        // Build lookup of all native emoji strings the dataset knows about
        const natives = new Set<string>();
        for (const emoji of Object.values(data.emojis)) {
          for (const skin of emoji.skins) {
            if (skin.native) natives.add(skin.native);
          }
        }
        knownNatives = natives;

        return emojiMart.init({ data });
      })
      .then(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <EmojiMartReadyContext.Provider value={ready}>{children}</EmojiMartReadyContext.Provider>;
}
